import { getCanElement } from '../core/five_elements.js';
import { getPersonalityProfile } from './personality_rules.js';

const DAI_VAN_TRENDS = {
  vi: {
    favorable: 'Thuận lợi',
    caution: 'Cần thận trọng',
    neutral: 'Trung tính'
  },
  en: {
    favorable: 'Favorable',
    caution: 'Use caution',
    neutral: 'Neutral'
  }
};

const RELATIONSHIP_COPY = {
  vi: {
    harmony: 'Nội lực có nhiều thế hợp, gia đạo dễ nhận được hỗ trợ.',
    caution: 'Có thế xung/hại nổi bật, nên ưu tiên lắng nghe và giảm đối đầu.',
    balance: 'Có một vài thế xung/hại, cần linh hoạt và chia sẻ đều.',
    neutral: 'Quan hệ gia đạo ổn định, cần duy trì giao tiếp đều đặn.'
  },
  en: {
    harmony: 'Strong harmonious patterns; family support comes more easily.',
    caution: 'Notable clashes; prioritize listening and de-escalation.',
    balance: 'Some friction patterns; stay flexible and communicate regularly.',
    neutral: 'Family dynamics are steady; keep consistent communication.'
  }
};

const CONCLUSION_COPY = {
  vi: {
    positive: 'Tổng thể thiên về ổn định và thuận lợi khi giữ kỷ luật.',
    neutral: 'Tổng thể ở mức cân bằng, cần tập trung đều các mảng chính.',
    caution: 'Cần ưu tiên cân bằng sức khỏe, sự nghiệp và tài chính để bền vững.'
  },
  en: {
    positive: 'Overall outlook is steady and favorable with discipline.',
    neutral: 'Overall outlook is balanced; keep steady focus on key areas.',
    caution: 'Prioritize balance across health, career, and finance for stability.'
  }
};

const getTrendLabel = (trendKey, language) => (
  DAI_VAN_TRENDS[language]?.[trendKey] || DAI_VAN_TRENDS.vi[trendKey]
);

const getCanFromLabel = (label) => {
  if (typeof label !== 'string') return null;
  const [canPart] = label.split(/[-\s]+/);
  return canPart || null;
};

const buildQuanHeSummaryLine = (quanHe, language = 'vi') => {
  if (!quanHe) return null;
  const hop = quanHe.hop?.length || 0;
  const xung = quanHe.xung?.length || 0;
  const hai = quanHe.hai?.length || 0;
  const hinh = quanHe.hinh?.length || 0;
  const pha = quanHe.pha?.length || 0;

  if (language === 'en') {
    return `Branch relations: Harmony ${hop}, Clash ${xung}, Harm ${hai}, Punishment ${hinh}, Break ${pha}.`;
  }
  return `Quan hệ chi: Hợp ${hop}, Xung ${xung}, Hại ${hai}, Hình ${hinh}, Phá ${pha}.`;
};

export const buildPersonalityParagraphs = ({
  dayMasterLabel,
  elementLabel,
  dayMasterElement,
  language = 'vi'
} = {}) => {
  const profile = getPersonalityProfile(dayMasterElement, language);
  if (!profile) return [];

  const coreTraits = profile.coreTraits.join(', ');
  const strengths = profile.strengths.join(', ');
  const risks = profile.risks.join(', ');
  const balanceAdvice = profile.balanceAdvice.join(', ');

  if (language === 'en') {
    return [
      `${dayMasterLabel} (${elementLabel}) tends to be ${coreTraits}.`,
      `Strengths: ${strengths}.`,
      `Risks: ${risks}.`,
      `Balance advice: ${balanceAdvice}.`
    ];
  }

  return [
    `Nhật chủ ${dayMasterLabel} (${elementLabel}) thiên về ${coreTraits}.`,
    `Điểm mạnh: ${strengths}.`,
    `Rủi ro: ${risks}.`,
    `Gợi ý cân bằng: ${balanceAdvice}.`
  ];
};

export const buildRuleBasedParagraphs = ({ reading, language = 'vi' } = {}) => {
  if (!reading || language !== 'vi') return [];

  const paragraphs = [];
  if (Number.isFinite(reading.rating)) {
    paragraphs.push(`Đánh giá tổng quan: ${reading.rating}/10.`);
  }
  if (reading.nhanXet) {
    paragraphs.push(`Nhận định: ${reading.nhanXet}.`);
  }
  if (reading.khuyenNghi?.length) {
    paragraphs.push(`Khuyến nghị: ${reading.khuyenNghi.join(', ')}.`);
  }

  return paragraphs;
};

export const buildRelationshipParagraphs = ({
  guidance,
  quanHe,
  language = 'vi'
} = {}) => {
  const paragraphs = [];
  if (guidance) paragraphs.push(guidance);
  const summaryLine = buildQuanHeSummaryLine(quanHe, language);
  if (summaryLine) paragraphs.push(summaryLine);
  if (!quanHe) return paragraphs;

  const hopCount = quanHe.hop?.length || 0;
  const tensionCount = (quanHe.xung?.length || 0)
    + (quanHe.hai?.length || 0)
    + (quanHe.hinh?.length || 0)
    + (quanHe.pha?.length || 0);

  if (hopCount >= 2) {
    paragraphs.push(RELATIONSHIP_COPY[language]?.harmony || RELATIONSHIP_COPY.vi.harmony);
  } else if (tensionCount >= 2) {
    paragraphs.push(RELATIONSHIP_COPY[language]?.caution || RELATIONSHIP_COPY.vi.caution);
  } else if (tensionCount === 1) {
    const tensionKey = ['xung', 'hai', 'hinh', 'pha']
      .find((key) => (quanHe[key]?.length || 0) > 0);
    const tensionLabel = {
      vi: {
        xung: 'Có 1 thế xung, cần linh hoạt và chia sẻ đều.',
        hai: 'Có 1 thế hại, nên lắng nghe và giảm đối đầu.',
        hinh: 'Có 1 thế hình, cần mềm dẻo và giao tiếp đều.',
        pha: 'Có 1 thế phá, nên ưu tiên ổn định và tránh va chạm.'
      },
      en: {
        xung: 'One clash pattern; stay flexible and communicate often.',
        hai: 'One harm pattern; listen more and avoid confrontation.',
        hinh: 'One punishment pattern; stay gentle and keep dialogue open.',
        pha: 'One break pattern; prioritize stability and minimize friction.'
      }
    };
    const fallback = RELATIONSHIP_COPY[language]?.balance || RELATIONSHIP_COPY.vi.balance;
    paragraphs.push((tensionKey && tensionLabel[language]?.[tensionKey]) || fallback);
  } else {
    paragraphs.push(RELATIONSHIP_COPY[language]?.neutral || RELATIONSHIP_COPY.vi.neutral);
  }

  return paragraphs;
};

export const buildRelationshipDeepParagraphs = ({
  quanHe,
  language = 'vi'
} = {}) => {
  if (!quanHe) {
    return language === 'en'
      ? ['Focus on stable routines and open communication.']
      : ['Ưu tiên nhịp sống ổn định và giao tiếp thẳng thắn.'];
  }

  const paragraphs = [];
  const hopCount = quanHe.hop?.length || 0;
  const xungCount = quanHe.xung?.length || 0;
  const haiCount = quanHe.hai?.length || 0;
  const hinhCount = quanHe.hinh?.length || 0;
  const phaCount = quanHe.pha?.length || 0;

  if (language === 'en') {
    if (hopCount >= 2) {
      paragraphs.push('Multiple harmony patterns suggest easier alignment in family life.');
    }
    if (xungCount + haiCount + hinhCount + phaCount >= 2) {
      paragraphs.push('Tension patterns (≥2) call for patience and empathy in relationships.');
    } else {
      paragraphs.push('Maintain steady communication to keep relationships balanced.');
    }
    const summaryLine = buildQuanHeSummaryLine(quanHe, language);
    if (summaryLine) paragraphs.push(summaryLine);
    return paragraphs;
  }

  if (hopCount >= 2) {
    paragraphs.push('Có nhiều thế hợp, gia đạo dễ đồng thuận nếu biết giữ nhịp.');
  }
  if (xungCount + haiCount + hinhCount + phaCount >= 2) {
    paragraphs.push('Xung/hại từ 2 cặp trở lên, cần kiên nhẫn và hạ nhiệt khi căng thẳng.');
  } else {
    paragraphs.push('Duy trì giao tiếp đều đặn để giữ cân bằng cảm xúc.');
  }
  const summaryLine = buildQuanHeSummaryLine(quanHe, language);
  if (summaryLine) paragraphs.push(summaryLine);

  return paragraphs.length ? paragraphs : [
    'Ưu tiên nhịp sống ổn định và lắng nghe nhiều hơn.'
  ];
};

export const buildConclusionParagraphs = ({
  healthReading,
  careerReading,
  wealthReading,
  language = 'vi'
} = {}) => {
  const ratings = [healthReading?.rating, careerReading?.rating, wealthReading?.rating]
    .filter((value) => Number.isFinite(value));
  const average = ratings.length
    ? ratings.reduce((sum, value) => sum + value, 0) / ratings.length
    : null;

  let tone = 'neutral';
  if (average !== null && average >= 8) tone = 'positive';
  if (average !== null && average <= 4) tone = 'caution';

  const summary = CONCLUSION_COPY[language]?.[tone] || CONCLUSION_COPY.vi[tone];

  if (language === 'en') {
    return [
      summary,
      average !== null ? `Overall score: ${average.toFixed(1)}/10.` : 'Overall score: —.'
    ];
  }

  return [
    summary,
    average !== null ? `Điểm tổng quan: ${average.toFixed(1)}/10.` : 'Điểm tổng quan: —.'
  ];
};

export const buildDaiVanRows = ({
  periods,
  dungThan = [],
  hyThan = [],
  kyThan = [],
  language = 'vi',
  formatElementLabel = (value) => value,
  labelKey = 'ageRange'
} = {}) => {
  if (!periods?.length) return [];
  const favorable = new Set([...dungThan, ...hyThan]);
  const caution = new Set(kyThan || []);

  return periods.map((period) => {
    const canValue = period.canChi?.can || getCanFromLabel(period.canChi?.label);
    const elementKey = getCanElement(canValue);
    const normalizedElement = elementKey || '';
    const elementLabel = formatElementLabel(normalizedElement) || '—';

    let trendKey = 'neutral';
    if (normalizedElement && favorable.has(normalizedElement)) trendKey = 'favorable';
    if (normalizedElement && caution.has(normalizedElement)) trendKey = 'caution';

    const trendLabel = getTrendLabel(trendKey, language);
    const rangeLabel = period[labelKey] || '';
    const detail = `${period.canChi.label} · ${elementLabel} · ${trendLabel}`;
    return [rangeLabel, detail];
  });
};
